import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  Trash2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ExternalLink,
  KeyRound,
  Shield,
  Search,
  Download,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../lib/api";
import { useNoIndex } from "../lib/useNoIndex";
import { useAuth } from "../lib/auth";

const ADMIN_OWNER_EMAIL = "pawgroundapp@gmail.com";

/**
 * Admin moderation dashboard at `/admin/submissions`.
 *
 * Auth model: this page is NOT tied to user sessions — it's gated by
 * the same `X-Admin-Token` header the backend admin endpoints already
 * require. The user enters the token once into a local form; we cache
 * it in `sessionStorage` (so closing the tab forgets it) and forward
 * it with every list/delete/approve/reject call. Backend will 403 if
 * the token is wrong, in which case we flip the UI back to the
 * unlock screen so the operator can retry.
 *
 * The page is intentionally not user-discoverable: no header link,
 * `useNoIndex` so crawlers skip it, and no token = no data is ever
 * shipped to the browser.
 */
const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "approved", label: "Approved" },
  { key: "pending", label: "Pending" },
  { key: "rejected", label: "Rejected" },
];

const SESSION_KEY = "pg_admin_token";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
};

const StatusPill = ({ status }) => {
  const map = {
    approved: { bg: "#E8F2EC", fg: "#4A7C59", label: "Approved" },
    pending: { bg: "#FFF7E6", fg: "#B45309", label: "Pending" },
    rejected: { bg: "#FCE8E6", fg: "#B91C1C", label: "Rejected" },
  };
  const c = map[status] || { bg: "#EEE", fg: "#555", label: status };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full font-body font-semibold"
      style={{ backgroundColor: c.bg, color: c.fg, fontSize: "11px", letterSpacing: "0.02em" }}
    >
      {c.label}
    </span>
  );
};

const AdminSubmissions = () => {
  // Robots: noindex. Operators discover this page via direct URL.
  useNoIndex();

  // Owner-gated extras (e.g. user-list CSV export) check the logged-in
  // user's email — distinct from the X-Admin-Token flow used by the
  // moderation queue itself.
  const { user } = useAuth();
  const isOwner = (user?.email || "").toLowerCase() === ADMIN_OWNER_EMAIL;
  const [exporting, setExporting] = useState(false);

  const [token, setToken] = useState(() => {
    try {
      return sessionStorage.getItem(SESSION_KEY) || "";
    } catch {
      return "";
    }
  });
  const [tokenInput, setTokenInput] = useState("");
  const [authError, setAuthError] = useState(null);

  const [statusFilter, setStatusFilter] = useState("all");
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Per-row pending action state. Maps id → "delete"|"approve"|"reject"
  // so the row can show a spinner without locking the whole grid.
  const [rowActions, setRowActions] = useState({});

  const loadSubmissions = useCallback(
    async (tkn, status) => {
      if (!tkn) return;
      setLoading(true);
      try {
        const { data } = await api.get("/places/suggest/pending", {
          params: { status: status || "all" },
          headers: { "X-Admin-Token": tkn },
        });
        setSubmissions(data?.submissions || []);
        setAuthError(null);
      } catch (err) {
        if (err?.response?.status === 403) {
          setAuthError("Invalid admin token");
          // Wipe the stored token so the unlock form shows again
          // immediately — operator can retry without a refresh.
          setToken("");
          try { sessionStorage.removeItem(SESSION_KEY); } catch {}
        } else {
          toast.error(err?.response?.data?.detail || "Couldn't load submissions");
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) loadSubmissions(token, statusFilter);
  }, [token, statusFilter, loadSubmissions]);

  const handleUnlock = (e) => {
    e.preventDefault();
    const t = tokenInput.trim();
    if (!t) {
      setAuthError("Token is required");
      return;
    }
    try { sessionStorage.setItem(SESSION_KEY, t); } catch {}
    setAuthError(null);
    setToken(t);
    setTokenInput("");
  };

  const handleLockOut = () => {
    try { sessionStorage.removeItem(SESSION_KEY); } catch {}
    setToken("");
    setSubmissions([]);
    setAuthError(null);
  };

  const runRowAction = async (id, action) => {
    // Map action → axios call. Each one re-uses the cached token from
    // state — the row UI handles its own optimistic-removal/refresh.
    if (!token) return;
    const prevActions = rowActions;
    setRowActions({ ...prevActions, [id]: action });
    try {
      if (action === "delete") {
        await api.delete(`/places/suggest/${id}`, {
          headers: { "X-Admin-Token": token },
        });
        // Optimistic remove — the row is gone for good.
        setSubmissions((rows) => rows.filter((r) => r.id !== id));
        toast.success("Submission deleted");
      } else if (action === "approve") {
        await api.patch(
          `/places/suggest/${id}/approve`,
          null,
          { headers: { "X-Admin-Token": token } }
        );
        setSubmissions((rows) =>
          rows.map((r) =>
            r.id === id
              ? { ...r, status: "approved", reviewed_at: new Date().toISOString() }
              : r
          )
        );
        toast.success("Submission approved");
      } else if (action === "reject") {
        await api.patch(
          `/places/suggest/${id}/reject`,
          null,
          { headers: { "X-Admin-Token": token } }
        );
        setSubmissions((rows) =>
          rows.map((r) =>
            r.id === id
              ? { ...r, status: "rejected", reviewed_at: new Date().toISOString() }
              : r
          )
        );
        toast.success("Submission rejected");
      }
    } catch (err) {
      if (err?.response?.status === 403) {
        toast.error("Admin token rejected — please re-enter.");
        handleLockOut();
      } else {
        toast.error(err?.response?.data?.detail || `Couldn't ${action} submission`);
      }
    } finally {
      setRowActions((curr) => {
        const next = { ...curr };
        delete next[id];
        return next;
      });
    }
  };

  // Owner-only: download a CSV of every signed-up user. We use the
  // user-session JWT (auto-attached by `api` axios instance) — NOT
  // the X-Admin-Token — so this works without the operator unlocking
  // the moderation queue first. We fetch via `responseType: 'blob'`
  // so binary payloads (large CSVs) stream cleanly into the URL the
  // browser downloads. The filename is read from the server's
  // Content-Disposition header when present, otherwise we fall back
  // to a sensible default.
  const handleExportUsers = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const res = await api.get("/admin/export-users", { responseType: "blob" });
      const dispo = res.headers?.["content-disposition"] || "";
      const match = dispo.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `pawground-users-${Date.now()}.csv`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Give the browser a tick to start the download before revoking
      // the object URL — Firefox in particular drops the download if
      // we revoke synchronously.
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("User list downloaded");
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Please sign in as the owner account first.");
      } else if (err?.response?.status === 403) {
        toast.error("Only the owner account can export users.");
      } else {
        toast.error("Couldn't download user list");
      }
    } finally {
      setExporting(false);
    }
  };

  // Client-side substring search across name/city/state/email. Keeps
  // moderation snappy on lists in the low hundreds without hitting the
  // backend on every keystroke.
  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return submissions;
    return submissions.filter((s) => {
      const hay = `${s.name} ${s.city} ${s.state} ${s.address} ${s.submitted_by_name || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [submissions, searchQuery]);

  // -- Unlock screen ---------------------------------------------------
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: "#F5F0E8" }}>
        <form
          onSubmit={handleUnlock}
          data-testid="admin-unlock-form"
          className="w-full max-w-md bg-white rounded-2xl border p-7 shadow-warm"
          style={{ borderColor: "#D4C9B0" }}
        >
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
            style={{ backgroundColor: "#E8F2EC" }}
          >
            <Shield className="w-6 h-6" style={{ color: "#4A7C59" }} />
          </div>
          <h1
            className="font-heading"
            style={{ fontSize: "22px", color: "#1A1A0F", fontWeight: 600 }}
          >
            Pawground admin
          </h1>
          <p
            className="mt-1 font-body"
            style={{ fontSize: "13px", color: "#7A7A60", lineHeight: 1.5 }}
          >
            Enter the admin token to manage community submissions.
          </p>

          <label className="block mt-5">
            <span
              className="font-body font-semibold mb-1.5 inline-block"
              style={{ fontSize: "13px", color: "#1A1A0F" }}
            >
              Admin token
            </span>
            <div className="relative">
              <KeyRound
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                style={{ color: "#7A7A60" }}
              />
              <input
                type="password"
                data-testid="admin-token-input"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="X-Admin-Token value"
                className="w-full"
                style={{
                  border: "1px solid #D4C9B0",
                  borderRadius: 8,
                  padding: "12px 12px 12px 36px",
                  fontFamily: "DM Sans, system-ui, sans-serif",
                  fontSize: 14,
                  color: "#1A1A0F",
                  background: "#FFFFFF",
                }}
                autoFocus
              />
            </div>
            {authError && (
              <p className="mt-2 font-body" style={{ fontSize: 12, color: "#C0392B" }}>
                {authError}
              </p>
            )}
          </label>

          <button
            type="submit"
            data-testid="admin-unlock-submit"
            className="mt-5 w-full font-body font-semibold rounded-lg active:scale-[0.97] transition-transform"
            style={{
              backgroundColor: "#4A7C59",
              color: "#FFFFFF",
              padding: "12px 16px",
              fontSize: 14,
            }}
          >
            Unlock dashboard
          </button>
        </form>
      </div>
    );
  }

  // -- Authenticated dashboard ----------------------------------------
  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F5F0E8" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1
              className="font-heading"
              style={{ fontSize: "24px", color: "#1A1A0F", fontWeight: 600 }}
            >
              Submitted places
            </h1>
            <p
              className="mt-1 font-body"
              style={{ fontSize: "13px", color: "#7A7A60" }}
            >
              {submissions.length} total · admin moderation
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isOwner && (
              <button
                type="button"
                data-testid="admin-export-users"
                onClick={handleExportUsers}
                disabled={exporting}
                className="inline-flex items-center gap-1.5 font-body font-semibold rounded-lg"
                style={{
                  background: "#4A7C59",
                  color: "#FFFFFF",
                  padding: "8px 14px",
                  fontSize: 13,
                  opacity: exporting ? 0.65 : 1,
                  cursor: exporting ? "not-allowed" : "pointer",
                }}
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {exporting ? "Preparing…" : "Download User List"}
              </button>
            )}
            <button
              type="button"
              data-testid="admin-refresh"
              onClick={() => loadSubmissions(token, statusFilter)}
              className="inline-flex items-center gap-1.5 font-body font-semibold rounded-lg border"
              style={{
                borderColor: "#D4C9B0",
                color: "#1A1A0F",
                padding: "8px 14px",
                fontSize: 13,
                background: "#FFFFFF",
              }}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              type="button"
              data-testid="admin-lockout"
              onClick={handleLockOut}
              className="inline-flex items-center gap-1.5 font-body font-semibold rounded-lg"
              style={{
                color: "#7A7A60",
                padding: "8px 14px",
                fontSize: 13,
                background: "transparent",
              }}
            >
              Lock out
            </button>
          </div>
        </div>

        {/* Filter row: status tabs + free-text search. Search is client-
            side so the operator can scrub the list as they type. */}
        <div
          className="bg-white rounded-2xl border p-4 mb-4 flex flex-wrap items-center gap-3"
          style={{ borderColor: "#D4C9B0" }}
        >
          <div className="flex items-center gap-1 flex-wrap">
            {STATUS_FILTERS.map((f) => {
              const active = statusFilter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  data-testid={`admin-filter-${f.key}`}
                  onClick={() => setStatusFilter(f.key)}
                  className="font-body font-semibold rounded-full transition-colors"
                  style={{
                    backgroundColor: active ? "#4A7C59" : "transparent",
                    color: active ? "#FFFFFF" : "#1A1A0F",
                    padding: "6px 14px",
                    fontSize: 13,
                    border: active ? "1px solid #4A7C59" : "1px solid #D4C9B0",
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={{ color: "#7A7A60" }}
            />
            <input
              type="text"
              data-testid="admin-search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Filter by name, city, state…"
              className="w-full"
              style={{
                border: "1px solid #D4C9B0",
                borderRadius: 8,
                padding: "8px 12px 8px 36px",
                fontFamily: "DM Sans, system-ui, sans-serif",
                fontSize: 13,
                color: "#1A1A0F",
                background: "#FFFFFF",
              }}
            />
          </div>
        </div>

        {/* List. Desktop shows a table-ish grid; mobile collapses each
            row to a stacked card. We render as <div>s instead of a real
            <table> so column widths can flex naturally and the action
            buttons can sit comfortably on every screen size. */}
        <div
          className="bg-white rounded-2xl border overflow-hidden"
          style={{ borderColor: "#D4C9B0" }}
        >
          {loading && submissions.length === 0 && (
            <div className="p-10 flex items-center justify-center gap-2 text-brand-ink/60">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading submissions…
            </div>
          )}
          {!loading && filteredRows.length === 0 && (
            <div
              data-testid="admin-empty"
              className="p-10 text-center font-body"
              style={{ color: "#7A7A60", fontSize: 14 }}
            >
              {submissions.length === 0
                ? "No submissions in this bucket yet."
                : "No submissions match that search."}
            </div>
          )}
          {filteredRows.map((s) => {
            const action = rowActions[s.id];
            const isCommunityUrl = s.slug ? `/place/${s.slug}` : null;
            return (
              <div
                key={s.id}
                data-testid={`admin-row-${s.id}`}
                className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_0.8fr_auto] gap-3 md:gap-4 px-5 py-4 border-t"
                style={{ borderColor: "#EFE6D2" }}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="font-body font-semibold truncate"
                      style={{ color: "#1A1A0F", fontSize: 14 }}
                      title={s.name}
                    >
                      {s.name}
                    </span>
                    <StatusPill status={s.status} />
                  </div>
                  <div
                    className="font-body mt-1 truncate"
                    style={{ fontSize: 12, color: "#7A7A60" }}
                  >
                    {s.address}
                  </div>
                  {s.why_dog_friendly && (
                    <div
                      className="font-body mt-1 line-clamp-2"
                      style={{ fontSize: 12, color: "#7A7A60", lineHeight: 1.4 }}
                    >
                      {s.why_dog_friendly}
                    </div>
                  )}
                </div>

                <div className="min-w-0 font-body" style={{ fontSize: 13, color: "#1A1A0F" }}>
                  <div>
                    <span style={{ color: "#7A7A60" }}>Category:</span> {s.category}
                  </div>
                  <div>
                    <span style={{ color: "#7A7A60" }}>City:</span> {s.city}, {s.state}
                  </div>
                  <div className="mt-1" style={{ fontSize: 12, color: "#7A7A60" }}>
                    by {s.submitted_by_name || "unknown"}
                  </div>
                </div>

                <div className="font-body" style={{ fontSize: 12, color: "#7A7A60" }}>
                  <div>Submitted</div>
                  <div style={{ color: "#1A1A0F", fontSize: 13 }}>{fmtDate(s.submitted_at)}</div>
                  {s.reviewed_at && s.reviewed_at !== s.submitted_at && (
                    <div className="mt-1">Reviewed {fmtDate(s.reviewed_at)}</div>
                  )}
                </div>

                <div className="flex items-start gap-2 flex-wrap md:justify-end">
                  {isCommunityUrl && (
                    <Link
                      to={isCommunityUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`admin-row-view-${s.id}`}
                      className="inline-flex items-center gap-1 font-body font-semibold rounded-lg border"
                      style={{
                        borderColor: "#D4C9B0",
                        color: "#1A1A0F",
                        padding: "6px 10px",
                        fontSize: 12,
                        background: "#FFFFFF",
                      }}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      View
                    </Link>
                  )}
                  {s.status !== "approved" && (
                    <button
                      type="button"
                      data-testid={`admin-row-approve-${s.id}`}
                      onClick={() => runRowAction(s.id, "approve")}
                      disabled={!!action}
                      className="inline-flex items-center gap-1 font-body font-semibold rounded-lg disabled:opacity-60"
                      style={{
                        backgroundColor: "#E8F2EC",
                        color: "#4A7C59",
                        padding: "6px 10px",
                        fontSize: 12,
                      }}
                    >
                      {action === "approve" ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      )}
                      Approve
                    </button>
                  )}
                  {s.status !== "rejected" && (
                    <button
                      type="button"
                      data-testid={`admin-row-reject-${s.id}`}
                      onClick={() => runRowAction(s.id, "reject")}
                      disabled={!!action}
                      className="inline-flex items-center gap-1 font-body font-semibold rounded-lg disabled:opacity-60"
                      style={{
                        backgroundColor: "#FFF7E6",
                        color: "#B45309",
                        padding: "6px 10px",
                        fontSize: 12,
                      }}
                    >
                      {action === "reject" ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5" />
                      )}
                      Reject
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid={`admin-row-delete-${s.id}`}
                    onClick={() => {
                      if (
                        // Guard rail — hard-delete is irreversible (cascades
                        // to reviews/checkins/busy/favorites for this id).
                        // Confirm with the actual place name to make
                        // skim-clicking impossible.
                        window.confirm(
                          `Hard-delete "${s.name}"? This also wipes any check-ins, reviews, busy reports, and favorites tied to it.`
                        )
                      ) {
                        runRowAction(s.id, "delete");
                      }
                    }}
                    disabled={!!action}
                    className="inline-flex items-center gap-1 font-body font-semibold rounded-lg disabled:opacity-60"
                    style={{
                      backgroundColor: "#FCE8E6",
                      color: "#B91C1C",
                      padding: "6px 10px",
                      fontSize: 12,
                    }}
                  >
                    {action === "delete" ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-4 font-body" style={{ fontSize: 12, color: "#7A7A60" }}>
          Hard-delete also removes any check-ins, reviews, busy reports, and
          favorites tied to the place id. Use Reject if you want to hide the
          listing but keep the audit trail.
        </p>
      </div>
    </div>
  );
};

export default AdminSubmissions;
